import { AssessmentSectionOverview } from '@components/pages/assessment-shell/components/assessment-section-overview.component';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { setReturnToSearchParam, useSafeReturnTo } from '@hooks/return-to.hook';
import { useGetCyberAssessment } from '@queries/cyber-assessment.query';
import {
  buildCyberAssessmentShellModel,
  isCyberAssessmentQuestionnaireEmpty,
} from '@utils/cyber-assessment/model.util';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CyberAssessmentEmptyState } from './cyber-assessment-empty-state.component';
import { CyberAssessmentLayout } from './cyber-assessment-layout.component';

export const CyberAssessmentPage: React.FC = () => {
  const { cyberAssessmentId, technologyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const chapterId = searchParams.get('chapterId') || undefined;
  const shouldViewFirstUnansweredQuestion = searchParams.get('viewFirstUnansweredQuestion') === 'true';
  const { data: cyberAssessment } = useGetCyberAssessment(cyberAssessmentId);
  const { returnTo } = useSafeReturnTo();
  const shellModel = useMemo(
    () => (cyberAssessment ? buildCyberAssessmentShellModel(cyberAssessment) : null),
    [cyberAssessment],
  );
  const currentChapter = shellModel?.chapters.find((chapter) => chapter.id === chapterId) ?? shellModel?.chapters[0];
  const currentChapterIndex = shellModel?.chapters.findIndex((chapter) => chapter.id === currentChapter?.id) ?? 0;

  useEffect(() => {
    if (shellModel?.firstUnansweredSectionId && shouldViewFirstUnansweredQuestion) {
      navigate(
        `/technology/${technologyId}/cyber-assessment/${cyberAssessmentId}/section/${shellModel.firstUnansweredSectionId}${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`,
        { replace: true },
      );
      return;
    }

    if (cyberAssessment && !chapterId && shellModel?.chapters.length) {
      const nextSearchParams = new URLSearchParams(searchParams);

      nextSearchParams.set('chapterId', shellModel.chapters[0].id);
      setReturnToSearchParam({ returnTo, searchParams: nextSearchParams });
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [
    chapterId,
    cyberAssessment,
    cyberAssessmentId,
    navigate,
    returnTo,
    searchParams,
    setSearchParams,
    shellModel,
    shouldViewFirstUnansweredQuestion,
    technologyId,
  ]);

  if (!cyberAssessment) {
    return null;
  }

  if (isCyberAssessmentQuestionnaireEmpty(cyberAssessment)) {
    return (
      <PageviewTracker>
        <HelmetWrapper title="Cyber Questionnaire" />
        <CyberAssessmentLayout cyberAssessment={cyberAssessment}>
          <CyberAssessmentEmptyState />
        </CyberAssessmentLayout>
      </PageviewTracker>
    );
  }

  if (!currentChapter) {
    return null;
  }

  return (
    <PageviewTracker>
      <HelmetWrapper title="Cyber Questionnaire" />
      <CyberAssessmentLayout
        currentChapterId={currentChapter.id}
        cyberAssessment={cyberAssessment}
      >
        <AssessmentSectionOverview
          chapterIndex={currentChapterIndex}
          onSectionSelect={(sectionId) =>
            navigate(
              `/technology/${technologyId}/cyber-assessment/${cyberAssessment.id}/section/${sectionId}${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`,
            )
          }
          sections={currentChapter.sections.map((section) => ({
            answered: section.answered,
            id: section.id,
            title: section.title,
            total: section.total,
          }))}
          title={currentChapter.title}
        />
      </CyberAssessmentLayout>
    </PageviewTracker>
  );
};
